"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ScrollingCamera = void 0;
const phaser_1 = __importDefault(require("phaser"));
const Utils_1 = require("./Utils");
class ScrollingCamera extends phaser_1.default.Cameras.Scene2D.Camera {
    /**
     * Creates an instance of ScrollingCamera.
     * @param  {Phaser.Scene} scene
     * @param  {scrollConfig} scrollConfig Contains scroll parameters
     * @memberof ScrollingCamera
     */
    constructor(scene, 
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    { x = 0, y = 0, width, height, top = 0, bottom = 5000, wheel = false, drag = 0.95, minSpeed = 4, snap = false, snapConfig = {
        topMargin: 50,
        padding: 50,
        deadZone: 0,
    }, }) {
        super(x, y, width, height);
        this.x = 0;
        this.y = 0;
        this.top = 0;
        this.bottom = 5000;
        this.wheel = false;
        this.drag = 0.95;
        this.minSpeed = 4;
        this.snap = false;
        this.snapConfig = {};
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.width = width || this.scene.game.config.width;
        this.height = height || this.scene.game.config.height;
        this.allowScroll = true;
        /**
         * Upper bound of the scroll
         * @type  {number}
         * @public
         */
        this.top = top;
        /**
         * Lower bound of the scroll
         * @type  {number}
         * @public
         */
        this.bottom = bottom - this.height;
        /**
         * Does this camera use the mouse wheel?
         * @type  {bool}
         * @public
         */
        this.wheel = wheel;
        /**
         * Number between 0 and 1.\n
         * Reduces the scroll speed per game step.\n
         * Example: 0.5 reduces 50% scroll speed per game step.
         * @type  {number}
         * @public
         */
        this.drag = drag;
        /**
         * Bellow this speed value (pixels/second), the scroll is stopped.
         * @type  {number}
         * @public
         */
        this.minSpeed = minSpeed;
        /**
         * Does this camera use snap points?
         * @type  {bool}
         * @public
         */
        this.snap = snap;
        /**
         * Contains snap effect parameters. Only used if snap parameter is true
         * @type  {snapConfig}
         * @public
         */
        this.snapGrid = snapConfig;
        /**
         * Determines if draging is active. Avoids residual movement after stop the scroll with the pointer.
         * Internal use.
         * @type {bool}
         * @private
         */
        this.moving = false;
        this.snapGrid.topMargin =
            snapConfig.topMargin === undefined ? 0 : snapConfig.topMargin;
        this.snapGrid.padding = snapConfig.padding || 20;
        this.snapGrid.deadZone =
            snapConfig.deadZone === undefined ? 0.4 : snapConfig.deadZone;
        this.setBounds(0, 0, this.scene.scale.width, bottom);
        this.wheelHandler = this.wheelHandler.bind(this);
        this.init();
    }
    init() {
        this.scrollY = this.top || this.y;
        this._rectangle = new phaser_1.default.Geom.Rectangle(this.x, this.y, this.width, this.height);
        // Vertical speed in pixels per second
        this._speed = 0;
        // scrollY value when drag action begins
        this._startY = this.scrollY;
        // scrollY value when drag action ends
        this._endY = this.scrollY;
        // timeStamp when drag action begins
        this._startTime = 0;
        // timeStamp when drag action ends
        this._endTime = 0;
        //// Sets events
        this.setDragEvent();
        if (this.wheel) {
            this.setWheelEvent();
        }
        this.scene.time.addEvent({
            delay: 500,
            callback: this.resetMoving,
            callbackScope: this,
            loop: true,
        });
        this.scene.cameras.addExisting(this);
    }
    resetMoving() {
        this.moving = false;
    }
    /**
     * Sets scroll speed. Use it to control scroll with any key or button.
     * @param  {number} speed Speed in pixels per second.
     * @memberof ScrollingCamera
     */
    setSpeed(speed) {
        if (typeof speed != 'number') {
            const distance = this._endY - this._startY; // pixels
            const duration = (this._endTime - this._startTime) / 1000; //seconds
            this._speed = distance / duration; // pixels/second
        }
        else {
            this._speed = speed;
        }
    }
    setDragEvent() {
        this.scene.input.on('pointermove', this.dragHandler, this);
        this.scene.input.on('pointerup', this.upHandler, this);
        this.scene.input.on('pointerdown', this.downHandler, this);
    }
    setWheelEvent() {
        var _a;
        (_a = this.scene) === null || _a === void 0 ? void 0 : _a.input.on('wheel', this.wheelHandler);
    }
    downHandler() {
        this._speed = 0;
        this._startY = this.scrollY;
        this._startTime = performance.now();
    }
    dragHandler(pointer) {
        if (pointer.isDown) {
            this.startY = this.scrollY;
            this.scrollY -= pointer.position.y - pointer.prevPosition.y;
            this.moving = true;
            if (this._bounds) {
                const isOver = this.scrollY + this.displayHeight > this._bounds.bottom;
                if (isOver) {
                    this.emit('onScrollEnd');
                }
            }
        }
    }
    upHandler() {
        this._endY = this.scrollY;
        this._endTime = performance.now();
        this.speed = 0;
        if (this.moving) {
            this.setSpeed();
        }
    }
    wheelHandler(event) {
        if (!this.scene || !this.allowScroll) {
            return;
        }
        if (this.isOver(this.scene.input.activePointer)) {
            this.scrollY += event.deltaY * Utils_1.Utils.scale;
        }
    }
    isOver(pointer) {
        return this._rectangle.contains(pointer.x, pointer.y);
    }
    clampScroll() {
        this.scrollY = phaser_1.default.Math.Clamp(this.scrollY, this.top, this.bottom);
        this._endY = this.scrollY;
    }
    update(_time, delta) {
        this.scrollY += this._speed * (delta / 1000);
        this._speed *= this.drag;
        if (Math.abs(this._speed) < this.minSpeed) {
            this._speed = 0;
            if (this.snap && !this.scene.input.activePointer.isDown) {
                const snapTop = this.top + this.snapGrid.topMargin;
                const snapPosition = this.scrollY - snapTop;
                const gap = this.snapGrid.padding;
                const gapRatio = snapPosition / gap;
                const gapRatioRemain = gapRatio % 1;
                if (Math.abs(0.5 - gapRatioRemain) >= this.snapGrid.deadZone / 2) {
                    this.scrollY = snapTop + Math.round(gapRatio) * gap;
                }
            }
        }
        // this.clampScroll();
    }
    disableScroll() {
        this.allowScroll = false;
        this.scene.input.off('pointermove', this.dragHandler, this);
        this.scene.input.off('pointerup', this.upHandler, this);
        this.scene.input.off('pointerdown', this.downHandler, this);
    }
    enableScroll() {
        this.allowScroll = true;
        this.setDragEvent();
    }
    destroy() {
        var _a;
        (_a = this.scene) === null || _a === void 0 ? void 0 : _a.input.off('wheel', this.wheelHandler);
        this.removeAllListeners();
        this.matrix.destroy();
        this.culledObjects = [];
        if (this._customViewport) {
            //  We're turning off a custom viewport for this Camera
            this.sceneManager.customViewports--;
        }
        this._bounds = null;
        this.scene = null;
        this.scaleManager = null;
        this.sceneManager = null;
    }
}
exports.ScrollingCamera = ScrollingCamera;
// ************************ TYPE DEFINITIONS *************************************
// *******************************************************************************
/**
 * Contains snap effect parameters
 * @typedef  {object} snapConfig
 * @property  {number} [topMargin = 0] Position y of the first snap point from the top.
 * @property  {number} [padding = 20] Vertical distance in pixels between snap points.
 * @property  {number} [deadZone = 0] % of space between two snap points not influenced by snap effect.\n
 * Example: 0.2 means 20% of middle space between two snap points is free of snap effect.
 */
/**
 * Contains all cameraScroll parameters
 * @typedef  {object} scrollConfig
 * @property  {number} [x = 0] The x position of this camera
 * @property  {number} [y = 0] The y position of this camera
 * @property  {number} [width = Phaser.game.config.width] The width of this camera
 * @property  {number} [height = Phaser.game.config.height] The height of this camera
 * @property  {number} [top = 0] Upper bound of the scroll
 * @property  {number} [bottom = 5000] Lower bound of the scroll
 * @property  {bool} [wheel = false] Does this camera use the mouse wheel?
 * @property  {number} [drag = 0.95] Number between 0 and 1.\n
 * Reduces the scroll speed per game step.\n
 * Example: 0.5 reduces 50% scroll speed per game step
 * @property  {number} [minSpeed] Bellow this speed value (pixels/second), the scroll is stopped
 * @property  {bool} [snap = false] Does this camera use snap points?
 * @property  {snapConfig} [snapConfig] Contains snap effect parameters. Only used if snap parameter is true
 */
